/**
 * Tokens do menu lateral derivados da marca (JOR4-7365).
 *
 * O protótipo do redesign usa dois tons que o tema não expõe: o verde-escuro do
 * fundo do menu (#043528) e o verde-claro do item ativo (#4fd6a8). Cravar esses
 * hex faria o menu ignorar o White Label, que reescreve
 * --dx-theme-primary-color e --dx-theme-secondary-color em runtime. Por isso são
 * calculados a partir da marca do tema em uso.
 *
 * O cálculo é feito em JS, e não com color-mix() no CSS, porque o color-mix
 * ainda não é seguro na base de navegadores do sistema — e porque este módulo já
 * é o dono do contrato de tokens em :root.
 */

type Rgb = [number, number, number];

/** Marca do tema padrão, usada quando o tema do cliente vem em formato inesperado. */
const PRIMARIA_PADRAO: Rgb = [6, 70, 53]; // #064635
const SECUNDARIA_PADRAO: Rgb = [28, 185, 135]; // #1cb987

/*
 * Fatores da derivação. Foram escolhidos para reproduzir o protótipo no tema
 * padrão: #064635 x 0,75 = #053528 (protótipo: #043528) e #1cb987 clareado 28%
 * = #5ccda9 (protótipo: #4fd6a8). A diferença é imperceptível e a regra vale
 * para qualquer marca, que é o ponto.
 *
 * Não há token de borda do menu: os separadores internos são brancos com alfa
 * (border-white/10, como no protótipo). Uma borda derivada da marca estourava em
 * marcas claras — um azul #1d4ed8 x 1,2 satura o canal e vira #235eff.
 */
const FATOR_FUNDO_MENU = 0.75;
const FATOR_CLAREAR_ATIVO = 0.28;

/*
 * Teto de luminância do fundo do menu. O texto do menu é branco com alfa entre
 * 0,55 e 0,65; numa marca clara, escurecer 25% não basta — um azul-claro
 * viraria um cinza sobre o qual o branco fica ilegível. O limite equivale a
 * mais de 6:1 de contraste contra branco puro, o que deixa folga para o alfa.
 *
 * O tema padrão fica bem abaixo disso (0,027), então nada muda para quem usa o
 * verde da marca: o teto só age em marcas claras.
 */
const LUMINANCIA_MAXIMA_FUNDO = 0.12;
const PASSO_ESCURECIMENTO = 0.9;
/** Salvaguarda: com o passo acima, qualquer cor cai abaixo do teto bem antes. */
const MAXIMO_PASSOS = 40;

/** Aceita #rgb e #rrggbb. Devolve null para qualquer outro formato. */
export const hexParaRgb = (hex: string | undefined): Rgb | null => {
    if (typeof hex !== "string") return null;

    const valor = hex.trim().replace(/^#/, "");

    if (valor.length === 3) {
        const [r, g, b] = valor.split("");
        return hexParaRgb(`${r}${r}${g}${g}${b}${b}`);
    }

    if (!/^[0-9a-f]{6}$/i.test(valor)) return null;

    return [
        parseInt(valor.slice(0, 2), 16),
        parseInt(valor.slice(2, 4), 16),
        parseInt(valor.slice(4, 6), 16),
    ];
};

const limitar = (valor: number): number =>
    Math.min(255, Math.max(0, Math.round(valor)));

// Sem padStart: o tsconfig usa lib "es6", que não declara métodos de ES2017.
const canalParaHex = (canal: number): string => {
    const hex = limitar(canal).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
};

const rgbParaHex = ([r, g, b]: Rgb): string =>
    `#${canalParaHex(r)}${canalParaHex(g)}${canalParaHex(b)}`;

/** Multiplica os canais: fator < 1 escurece, fator > 1 clareia. */
const escalar = (rgb: Rgb, fator: number): Rgb =>
    rgb.map((canal) => limitar(canal * fator)) as Rgb;

/** Luminância relativa da WCAG, usada para garantir contraste com o texto. */
const luminanciaRelativa = ([r, g, b]: Rgb): number => {
    const linear = (canal: number) => {
        const s = canal / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
};

/**
 * Escurece até o fundo aguentar texto branco. Devolve a cor intacta quando ela
 * já é escura o bastante, que é o caso da esmagadora maioria das marcas.
 */
const escurecerAteCaber = (rgb: Rgb): Rgb => {
    let resultado = rgb;

    for (let passo = 0; passo < MAXIMO_PASSOS; passo++) {
        if (luminanciaRelativa(resultado) <= LUMINANCIA_MAXIMA_FUNDO) break;
        resultado = escalar(resultado, PASSO_ESCURECIMENTO);
    }

    return resultado;
};

/** Aproxima do branco na proporção informada (0 = cor original, 1 = branco). */
const clarear = (rgb: Rgb, proporcao: number): Rgb =>
    rgb.map((canal) => limitar(canal + (255 - canal) * proporcao)) as Rgb;

/**
 * Monta as custom properties do menu a partir das cores do tema. Cores em
 * formato inesperado caem no tema padrão em vez de gerar token inválido — um
 * `rgba(NaN, ...)` deixaria o menu sem fundo.
 */
export const montarTokensDerivados = (theme: {
    primary_color?: string;
    secondary_color?: string;
}): Record<string, string> => {
    /*
     * Fallback em par, e não cor a cor. Um tema com primária válida e secundária
     * quebrada produziria o menu na marca do cliente com o destaque no verde
     * padrão — combinação que ninguém escolheu e que passa por bug de tela.
     * Se uma falha, as duas voltam ao padrão.
     */
    const primariaLida = hexParaRgb(theme.primary_color);
    const secundariaLida = hexParaRgb(theme.secondary_color);
    const parValido = primariaLida !== null && secundariaLida !== null;

    const primaria = parValido ? primariaLida! : PRIMARIA_PADRAO;
    const secundaria = parValido ? secundariaLida! : SECUNDARIA_PADRAO;

    return {
        // Componentes RGB para compor cores com alfa: rgba(var(--...-rgb), .2).
        "--eponto-primary-rgb": primaria.join(", "),
        "--eponto-secondary-rgb": secundaria.join(", "),

        "--eponto-sidebar-bg": rgbParaHex(
            escurecerAteCaber(escalar(primaria, FATOR_FUNDO_MENU)),
        ),
        "--eponto-sidebar-active-fg": rgbParaHex(
            clarear(secundaria, FATOR_CLAREAR_ATIVO),
        ),
    };
};
